import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { useGlobalStore } from 'stores/global.js'

const CACHE_TTL = 5 * 60 * 1000

// The sidebar shows sections for enabled integrations, so whenever the list changes
// the global store is told which ones the user has switched on.
function syncEnabledIntegrations(integrations) {
  const names = integrations.filter((i) => i.user_integration_id).map((i) => i.locale_name)
  useGlobalStore().setEnabledIntegrations(names)
}

export const useIntegrationsStore = defineStore('integrations', {
  state: () => ({
    integrations: [],
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,

    connectedCount: (state) =>
      state.integrations.filter((i) => i.user_integration_id !== null).length,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.integrations.length > 0) return
      await this.fetchIntegrations()
    },

    async fetchIntegrations() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/integrations', {}, false)
        if (!res.js_error && res.response.success) {
          this.integrations = res.response.data
          this.fetchedAt = Date.now()
          syncEnabledIntegrations(this.integrations)
        } else {
          this.error = 'Failed to load integrations.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    updateIntegration(integrationId, patch) {
      const idx = this.integrations.findIndex((i) => i.integration_id === integrationId)
      if (idx !== -1) Object.assign(this.integrations[idx], patch)
    },

    setAll(integrations) {
      this.integrations = integrations
      this.fetchedAt = Date.now()
      syncEnabledIntegrations(integrations)
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
